import { FormEvent } from "react"
import { faCalendarDay, faClose, faListAlt } from "@fortawesome/free-solid-svg-icons"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import ModalPortal from "../../../components/Modal/ModalPortal"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import { CourierPaymentRecord } from "../../../Objects/DeliveryEntity/CourierPaymentRecord"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import THEME from "../../../configuration/Theme"
import { SIZE } from "../../../configuration/Sizes"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import InputField from "../../../components/InputField/InputField"
import { StatusBadge } from "../Components/StatusBadge"
import { usePermission } from "../../../hooks/Permissions/usePermission"
import { Permissions } from "../../../Enum/PermissionsType"

interface CourierPaymentStatementPupopProps {
  rows: CourierPaymentRecord[]
  title: string
  startDate: string
  endDate: string
  onClose: () => void
  onApplyFilter: () => void
  onChangeStartDate: (value: string) => void
  onChangeEndDate: (value: string) => void
}

export const CourierPaymentStatementPupop = ({
  rows,
  title,
  startDate,
  endDate,
  onClose,
  onApplyFilter,
  onChangeStartDate,
  onChangeEndDate,
}: CourierPaymentStatementPupopProps) => {
  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    onApplyFilter()
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert2}>
            <TitlePageResponsiveIconSmall
              title={title}
              icon={faListAlt}
              boxShadow="none"
              iconColor={THEME.colors.iconBGColor}
              children={
                <ButtonIcon
                  sizeNumber={SIZE.buttonCloseIconSize}
                  bgColor={THEME.colors.buttonBGCloseColor}
                  iconColor={THEME.colors.buttonFGCloseColor}
                  icon={faClose}
                  onClick={onClose}
                  size="small"
                  descriptionKey="interactionDescriptions:pages.delivery.pupop.courierPaymentStatementPupop.button.onClose"
                />
              }
            />

            <div className="space-y-4 overflow-y-auto p-2">
              <form onSubmit={handleSubmit} className="grid gap-3 md:grid-cols-[1fr_1fr_auto]">
                <InputField
                  title="Data inicial"
                  type="date"
                  value={startDate}
                  onChange={(e) => onChangeStartDate(e.target.value)}
                  placeholder="Data inicial"
                  icon={faCalendarDay}
                  descriptionKey="interactionDescriptions:pages.delivery.pupop.courierPaymentStatementPupop.input.dataInicial"
                />

                <InputField
                  title="Data final"
                  type="date"
                  value={endDate}
                  onChange={(e) => onChangeEndDate(e.target.value)}
                  placeholder="Data final"
                  icon={faCalendarDay}
                  descriptionKey="interactionDescriptions:pages.delivery.pupop.courierPaymentStatementPupop.input.dataFinal"
                />

                <div className="flex items-end">
                  <Button
                    bgColor={THEME.colors.buttonBGSaveColor}
                    label="Buscar extrato"
                    onClick={onApplyFilter}
                    descriptionKey="interactionDescriptions:pages.delivery.pupop.courierPaymentStatementPupop.button.buscarExtrato"
                  />
                </div>
              </form>

              <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
                <div className="overflow-hidden rounded-xl border border-slate-200">
                  <div className="hidden text-center md:grid md:grid-cols-[1.2fr_1fr_1fr_1fr_1.2fr] gap-3 bg-slate-50 px-4 py-3 text-xs font-bold uppercase text-slate-500">
                    <span>Loja</span>
                    <span>Data e hora</span>
                    <span>Valor</span>
                    <span>Status</span>
                    <span>Observação</span>
                  </div>

                  <div className="divide-y divide-slate-200">
                    <Animate type={AnimationTypes.fadeInTraslate} duration="1.8s">
                      {rows.length === 0 && (
                        <div className="p-4 text-center text-sm text-slate-500">
                          Nenhum pagamento encontrado para o período informado.
                        </div>
                      )}
                    </Animate>

                    {rows.map((row, index) => (
                      <Animate key={row.id} type={AnimationTypes.fadeInTraslate} duration={`${0.2 + index * 0.1}s`}>
                        <div className="grid gap-3 px-4 py-4 text-center text-sm md:grid-cols-[1.2fr_1fr_1fr_1fr_1.2fr] md:items-center">
                          <div>
                            <p className="font-semibold text-slate-900">{row.companyName}</p>
                            <p className="text-xs text-slate-500">{row.notes || "Sem observações"}</p>
                          </div>

                          <div className="text-[12px] font-bold">{row.paymentDate.toString().toReadableDate()}</div>

                          <div className="font-semibold text-slate-900">{row.amount.toMoney()}</div>

                          <div>
                            <StatusBadge status={row.confirmationStatus} />
                          </div>

                          <div className="text-slate-600">{row.notes || "Sem observações"}</div>
                        </div>
                      </Animate>
                    ))}
                  </div>
                </div>
              </Animate>
            </div>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
